import { useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import UploadIcon from '@mui/icons-material/Upload';
import * as XLSX from 'xlsx';
import { useNotify, useRefresh } from "react-admin";
import { API } from "../providers/api.provider";
import { Alert, Box, CircularProgress, Link, Typography } from "@mui/material";

/**
 * Formato esperado da planilha XLSX:
 * 
 * Colunas obrigatórias:
 * - usuario_id: ID do usuário (UUID)
 * - produto_id: ID do produto (UUID)
 * - versao: Versão do produto (número)
 * - codigo_serial: Código do serial da licença
 * 
 * Colunas opcionais:
 * - tempo_uso: Tempo de uso em dias (número)
 * - data_aquisicao: Data de aquisição (formato: YYYY-MM-DD ou DD/MM/YYYY)
 * - ativacoes_contratadas: Número de ativações contratadas (número)
 * - ativacoes_restantes: Número de ativações restantes (número)
 * - codigo_reset: Código de reset
 * - plug: Se é plug (SIM/NÃO ou TRUE/FALSE ou 1/0)
 * - valor_venda: Valor da venda (número)
 * - parcelas_vendas: Número de parcelas (número)
 * - metodo_pagamento: Método de pagamento (texto)
 */

interface LicenseImportRow {
  usuario_id: string;
  produto_id: string;
  versao: string | number;
  codigo_serial: string;
  tempo_uso?: string | number;
  data_aquisicao?: string;
  ativacoes_contratadas?: string | number;
  ativacoes_restantes?: string | number;
  codigo_reset?: string;
  plug?: string | number | boolean;
  valor_venda?: string | number;
  parcelas_vendas?: string | number;
  metodo_pagamento?: string;
}

export const ImportLicensesButton = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const notify = useNotify();
  const refresh = useRefresh();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const downloadTemplate = () => {
    // Criar planilha template
    const templateData = [
      {
        usuario_id: "08120fc3-af51-49d3-85bc-43572ff88507",
        produto_id: "1c7f931f-acca-457d-a73f-eb3ea65ae9ca",
        versao: 1,
        codigo_serial: "XXXX-XXXX-XXXX-XXXX",
        tempo_uso: 365,
        data_aquisicao: "2026-01-30",
        ativacoes_contratadas: 5,
        ativacoes_restantes: 3,
        codigo_reset: "RESET-CODE-123",
        plug: "SIM",
        valor_venda: 1500.00,
        parcelas_vendas: 12,
        metodo_pagamento: "Cartão de Crédito"
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Licenças");
    XLSX.writeFile(wb, "template_licencas.xlsx");
  };

  const parseDate = (dateStr: string): string | undefined => {
    if (!dateStr) return undefined;
    
    // Tenta parsear diferentes formatos de data
    const dateValue = dateStr.toString().trim();
    
    // Formato DD/MM/YYYY
    if (dateValue.includes('/')) {
      const [day, month, year] = dateValue.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    // Formato YYYY-MM-DD (já está correto)
    if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateValue;
    }
    
    return undefined;
  };

  const parseBoolean = (value: string | number | boolean): boolean => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    
    const str = value.toString().toLowerCase().trim();
    return str === 'sim' || str === 'true' || str === '1';
  };

  const parseLicenseData = (row: LicenseImportRow) => {
    const licenseData: any = {
      userId: row.usuario_id.toString().trim(),
      productId: row.produto_id.toString().trim(),
      version: Number(row.versao),
      serialNumber: row.codigo_serial.toString(),
    };

    // Campos opcionais
    if (row.tempo_uso) {
      licenseData.usageTime = Number(row.tempo_uso);
    }

    if (row.data_aquisicao) {
      const parsedDate = parseDate(row.data_aquisicao.toString());
      if (parsedDate) {
        licenseData.acquisition = parsedDate;
      }
    }

    if (row.ativacoes_contratadas) {
      licenseData.contractedActivations = Number(row.ativacoes_contratadas);
    }

    if (row.ativacoes_restantes) {
      licenseData.remainingActivations = Number(row.ativacoes_restantes);
    }

    if (row.codigo_reset) {
      licenseData.resetCode = row.codigo_reset.toString();
    }

    if (row.plug !== undefined) {
      licenseData.plug = parseBoolean(row.plug);
    }

    if (row.valor_venda) {
      licenseData.valorVenda = Number(row.valor_venda);
    }

    if (row.parcelas_vendas) {
      licenseData.parcelasVendas = Number(row.parcelas_vendas);
    }

    if (row.metodo_pagamento) {
      licenseData.metodoPagamento = row.metodo_pagamento.toString();
    }

    return licenseData;
  };

  const handleImport = async () => {
    if (!file) {
      notify('Selecione um arquivo para importar', { type: 'warning' });
      return;
    }

    setLoading(true);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData: LicenseImportRow[] = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        notify('A planilha está vazia', { type: 'warning' });
        setLoading(false);
        return;
      }

      // Validar campos obrigatórios
      const errors: string[] = [];
      jsonData.forEach((row, index) => {
        if (!row.usuario_id) errors.push(`Linha ${index + 2}: ID do usuário é obrigatório`);
        if (!row.produto_id) errors.push(`Linha ${index + 2}: ID do produto é obrigatório`);
        if (!row.versao) errors.push(`Linha ${index + 2}: Versão é obrigatória`);
        if (!row.codigo_serial) errors.push(`Linha ${index + 2}: Código do serial é obrigatório`);
      });

      if (errors.length > 0) {
        notify(`Erros na planilha:\n${errors.slice(0, 5).join('\n')}`, { type: 'error' });
        setLoading(false);
        return;
      }

      // Processar e enviar dados
      const licensesData = jsonData.map(parseLicenseData);

      // Enviar para o backend
      await API.post('/admin/licenses/import', { licenses: licensesData });

      notify(`${licensesData.length} licença(s) importada(s) com sucesso`, { type: 'success' });
      setOpen(false);
      setFile(null);
      refresh();
    } catch (error: any) {
      console.error('Erro ao importar licenças:', error);
      notify(error?.response?.data?.message || 'Erro ao importar licenças', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        color="primary"
        onClick={() => setOpen(true)}
      >
        <UploadIcon style={{ marginRight: 8 }} />
        Importar
      </Button>

      <Dialog open={open} onClose={() => !loading && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Importar Licenças</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Alert severity="info">
              <Typography variant="body2" gutterBottom>
                <strong>Formato do arquivo:</strong>
              </Typography>
              <Typography variant="body2" component="div">
                • Arquivo Excel (.xlsx)<br />
                • Colunas obrigatórias: usuario_id, produto_id, versao, codigo_serial<br />
                • Colunas opcionais: tempo_uso, data_aquisicao, ativacoes_contratadas, ativacoes_restantes, codigo_reset, plug, valor_venda, parcelas_vendas, metodo_pagamento
              </Typography>
              <Box mt={1}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={downloadTemplate}
                  style={{ cursor: 'pointer' }}
                >
                  Baixar planilha modelo
                </Link>
              </Box>
            </Alert>
          </Box>

          <input
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            id="import-licenses-file"
            type="file"
            onChange={handleFileChange}
          />
          <label htmlFor="import-licenses-file">
            <Button variant="outlined" component="span" fullWidth disabled={loading}>
              {file ? file.name : 'Selecionar arquivo'}
            </Button>
          </label>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleImport}
            color="primary"
            variant="contained"
            disabled={!file || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Importar'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
